import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Guest } from '../types';
import { FileUpload } from './FileUpload';
import { UploadedFile } from '../services/fileUploadService';

interface GuestFormData {
  name: string;
  phone: string;
  email: string;
  tcPassport: string;
  passportFile?: string;
}

const emptyGuest = (): GuestFormData => ({
  name: '',
  phone: '',
  email: '',
  tcPassport: '',
  passportFile: undefined,
});

interface GuestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGuestsAdded: (guests: Guest[]) => void;
  existingGuests?: Guest[];
}

export const GuestModal: React.FC<GuestModalProps> = ({
  isOpen,
  onClose,
  onGuestsAdded,
  existingGuests,
}) => {
  const { t } = useTranslation();
  const [guests, setGuests] = useState<GuestFormData[]>([emptyGuest()]);
  const [errors, setErrors] = useState<Record<number, string>>({});

  // Initialize with existing guests when modal opens
  useEffect(() => {
    if (isOpen) {
      if (existingGuests && existingGuests.length > 0) {
        setGuests(
          existingGuests.map((g) => ({
            name: g.name || '',
            phone: g.phone || '',
            email: g.email || '',
            tcPassport: g.tcPassport || '',
            passportFile: g.passportFile,
          }))
        );
      } else {
        setGuests([emptyGuest()]);
      }
      setErrors({});
    }
  }, [isOpen, existingGuests]);

  const updateGuest = (index: number, field: keyof GuestFormData, value: string) => {
    setGuests((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    // Clear error for this guest when they type
    if (field === 'name' && value.trim()) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  };

  const handleFileChange = (index: number, file: UploadedFile | null) => {
    setGuests((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], passportFile: file?.url };
      return updated;
    });
  };

  const addGuest = () => {
    setGuests((prev) => [...prev, emptyGuest()]);
  };

  const removeGuest = (index: number) => {
    if (guests.length <= 1) return;
    setGuests((prev) => prev.filter((_, i) => i !== index));
    // Re-index errors
    setErrors((prev) => {
      const updated: Record<number, string> = {};
      Object.entries(prev).forEach(([key, val]) => {
        const k = parseInt(key);
        if (k < index) updated[k] = val;
        else if (k > index) updated[k - 1] = val;
      });
      return updated;
    });
  };

  const handleSave = () => {
    // Validate: every guest must have a name
    const newErrors: Record<number, string> = {};
    guests.forEach((g, i) => {
      if (!g.name.trim()) {
        newErrors[i] = t('forms.nameRequired');
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const result: Guest[] = guests.map((g) => ({
      name: g.name.trim(),
      phone: g.phone || undefined,
      email: g.email || undefined,
      tcPassport: g.tcPassport || undefined,
      passportFile: g.passportFile,
    }));

    onGuestsAdded(result);
    onClose();
  };

  const handleClose = () => {
    setGuests([emptyGuest()]);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            {existingGuests && existingGuests.length > 0
              ? t('guest.editGuests')
              : t('guest.addGuest')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Guest Forms */}
        <div className="space-y-4">
          {guests.map((guest, index) => (
            <div
              key={index}
              className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 relative"
            >
              {/* Guest Header */}
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('guest.passenger')} {index + 1}
                </h3>
                {guests.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeGuest(index)}
                    className="text-red-500 hover:text-red-700 text-sm font-medium flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {t('guest.removeGuest')}
                  </button>
                )}
              </div>

              {/* Name - Always shown */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('guest.name')} *
                </label>
                <input
                  type="text"
                  value={guest.name}
                  onChange={(e) => updateGuest(index, 'name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white ${
                    errors[index] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder={t('forms.fullNamePlaceholder')}
                />
                {errors[index] && (
                  <p className="text-red-500 text-sm mt-1">{errors[index]}</p>
                )}
              </div>

              {/* First guest: show all fields (phone, email, TC/passport, file upload) */}
              {index === 0 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('guest.phone')}
                      </label>
                      <input
                        type="tel"
                        value={guest.phone}
                        onChange={(e) => updateGuest(index, 'phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
                        placeholder="+90 555 123 4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('guest.email')}
                      </label>
                      <input
                        type="email"
                        value={guest.email}
                        onChange={(e) => updateGuest(index, 'email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
                        placeholder="guest@example.com"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('guest.tcPassport')}
                    </label>
                    <input
                      type="text"
                      value={guest.tcPassport}
                      onChange={(e) => updateGuest(index, 'tcPassport', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
                      placeholder="12345678901"
                    />
                  </div>

                  <FileUpload
                    value={guest.passportFile}
                    onChange={(file) => handleFileChange(index, file)}
                    folder="passports"
                    label={t('guest.passportFile')}
                    hint={t('fileUpload.passportHint')}
                    accept="image/*,.pdf"
                  />
                </>
              )}

              {/* Other guests: only TC/passport field in addition to name */}
              {index > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('guest.tcPassport')}
                  </label>
                  <input
                    type="text"
                    value={guest.tcPassport}
                    onChange={(e) => updateGuest(index, 'tcPassport', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
                    placeholder="12345678901"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Another Guest Button */}
        <button
          type="button"
          onClick={addGuest}
          className="mt-4 w-full py-2.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 font-medium transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t('guest.addAnotherGuest')}
        </button>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 font-medium min-h-[44px] transition-colors text-gray-700 dark:text-gray-300"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 font-medium min-h-[44px] transition-colors"
          >
            {t('guest.saveGuests')} ({guests.length})
          </button>
        </div>
      </div>
    </div>
  );
};
